import React from "react";
import { useLocation } from "react-router-dom";
import { ImmunizationRecord, ImmunizationSeries } from "../../models/Interfaces";
import EventCard from "../../components/EventCard";
import getIzProvider, { IzProvider } from "../../models/providers/IzProviders";
import { getStatusColor, getStatusLabel } from "../../utils/status";
import "./events.css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TFunction } from "i18next";
import { getDateTime } from "../../utils/data";

interface EventLocationState {
  record?: ImmunizationRecord;
  series?: ImmunizationSeries;
}

const calcDueDateText = (immSeries: ImmunizationSeries | null | undefined) => {
  const { t } = useTranslation();
  if (!immSeries) {
    return "";
  }

  if (immSeries.dateDue && immSeries.dateOverdue) {
    return (
      <>
        {getDateTime(immSeries.dateDue).toLocaleString()} -{" "}
        {getDateTime(immSeries.dateOverdue).toLocaleString()}
      </>
    );
  }
  if (immSeries.dateOverdue) {
    return (
      <>
        <div className="text-bold">{t("immunizations.records_overdue_after")} </div>
        {getDateTime(immSeries.dateOverdue).toLocaleString()}
      </>
    );
  }
  if (immSeries.dateDue) {
    return (
      <>
        <div className="text-bold">{t("immunizations.records_due_on_or_after")} </div>
        {getDateTime(immSeries.dateDue).toLocaleString()}
      </>
    );
  }

  return "";
};

export const EventHeaderCard = ({
  immSeries,
  provider,
  t,
}: {
  immSeries: ImmunizationSeries;
  provider: IzProvider;
  t: TFunction;
}) => {
  const dueDate = calcDueDateText(immSeries);
  const color = getStatusColor(provider, immSeries?.status.toLowerCase(), immSeries);
  const label = t("immunizations.records_status", {
    context: getStatusLabel(provider, immSeries?.status.toLowerCase(), immSeries),
  });
  return (
    <div
      className="event-card-header flex flex-col md:docket-gap-1 py-2 max-md:gap-0.5"
      style={{ backgroundColor: "#3d7bb3" }}
    >
      <p className="text-2xl font-semibold text-white">{immSeries.vaccineType}</p>
      <div
        className={`${color} status-badge font-bold flex items-center w-fit mx-auto p-2 max-md:hidden`}
      >
        <p className="text-white">
          {label.toUpperCase()}
          {dueDate && <p>{dueDate}</p>}
        </p>
      </div>
      <div className={`${color} status-badge md:hidden w-fit px-2 mx-auto`}>
        <p className="text-white text-sm">{label.toUpperCase()}</p>
      </div>
      <p className="md:hidden text-white text-sm">{dueDate && <p>{dueDate}</p>}</p>
    </div>
  );
};

export default function Events() {
  const { state } = useLocation();
  const { record, series } = (state as EventLocationState) || {};
  const { t } = useTranslation();

  const provider = record?.izProviderKey ? getIzProvider(record.izProviderKey, t) : undefined;
  return (
    <div className="flex flex-col space-y-4 max-md:px-4 max-md:pb-20 max-md:pt-6">
      <div className="flex">
        <div className="md:pb-8 max-md:hidden w-1/5">
          <Link to="/home/records" className="docket-link">
            <img src="/assets/images/chevron-left-solid.svg" className="h-6 mr-auto" />
          </Link>
        </div>
        <h2 className="text-dark dark:text-white text-xl font-black max-md:hidden w-3/5 text-center">
          {`${record?.firstName || ""} ${record?.lastName || ""}`}
        </h2>
        <div className="max-md:hidden w-1/5"></div>
      </div>
      {provider && series && <EventHeaderCard immSeries={series} provider={provider} t={t} />}
      <div className="flex flex-col md:gap-3 max-md:gap-3">
        {series?.events?.map((e, index) => (
          <EventCard index={index} event={e} vaccine={series.vaccineType} />
        ))}
      </div>
    </div>
  );
}
